import * as leaflet from 'leaflet';

(window as any).L = leaflet;

// eslint-disable-next-line @typescript-eslint/no-require-imports
require('leaflet.markercluster');
// eslint-disable-next-line @typescript-eslint/no-require-imports
require('leaflet-draw');

const L: typeof leaflet & {
    markerClusterGroup(options?: any): any;
    Draw: {
        Event: {
            CREATED: string;
            EDITED: string;
            DELETED: string;
        };
    };
    Control: typeof leaflet.Control & {
        Draw: new (options?: any) => leaflet.Control;
    };
} = (window as any).L;
import {
    LatLng,
    MapOptions,
    MarkerOptions,
    PolylineOptions,
    PolylineStyleOptions,
    PolygonOptions,
    MapBounds,
    ClusterOptions,
    ClusteringType,
    MapEventType,
    MapEvent,
    IMapProvider
} from 'app/shared/map/interfaces';
import {decodePolyline, decodeMultiplePolylines, toLeafletCoordinates} from 'app/shared/map/utils/polyline-decoder.util';
import {environment} from 'environments/environment';

/**
 * Leaflet Map Provider
 * Handles all Leaflet-specific map operations
 */
export class LeafletMapProvider implements IMapProvider {
    private map: leaflet.Map | null = null;
    private markers: Map<string, leaflet.Marker> = new Map();
    private polylines: Map<string, leaflet.Polyline | leaflet.Polyline[]> = new Map();
    private polygons: Map<string, leaflet.Polygon> = new Map();
    private groups: Map<string, leaflet.FeatureGroup> = new Map();
    private markerClusterGroup: any = null; // L.MarkerClusterGroup from plugin
    private drawnItems: leaflet.FeatureGroup | null = null;
    private drawControl: leaflet.Control | null = null; // L.Control.Draw from plugin
    private layerControl: leaflet.Control | null = null; // Custom layer switcher control
    private scaleControl: leaflet.Control | null = null; // Custom scale control
    private eventHandlers: Map<string, ((e: MapEvent) => void)[]> = new Map();
    private idCounter = 0;
    private baseLayers: Map<string, leaflet.TileLayer> = new Map();
    private currentBaseLayer: leaflet.TileLayer | null = null;

    // Traffic overlay feature groups (HERE Traffic API v7)
    private trafficFlowGroup: leaflet.FeatureGroup | null = null;
    private trafficIncidentsGroup: leaflet.FeatureGroup | null = null;
    private trafficFlowEnabled: boolean = false;
    private trafficIncidentsEnabled: boolean = false;
    private trafficFetchDebounceTimer: any = null;
    private trafficFlowAbortController: AbortController | null = null;
    private trafficIncidentsAbortController: AbortController | null = null;

    private clusteringType: ClusteringType = ClusteringType.COUNT;
    private valueExtractor: ((markerData: any) => number) | null = null;
    private popupOpen = false;

    // Phones and tablets have no hover: a tap fires the emulated mouseover and then the click, so a popup that opens
    // on mouseover and closes on click never stays open. On those devices the tap opens it instead.
    private readonly isTouchOnly = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: none)').matches;

    // Keeps an opened popup clear of the zoom / layer controls in the map's top-left corner on a touch screen.
    private get popupPanPadding(): Partial<leaflet.PopupOptions> {
        return this.isTouchOnly ? {autoPanPaddingTopLeft: leaflet.point(8, 96), autoPanPaddingBottomRight: leaflet.point(8, 8)} : {};
    }

    private readonly OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
    private readonly OSM_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
    private readonly CLEAN_OSM_TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
    private readonly CLEAN_OSM_ATTRIBUTION =
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>';
    private readonly SATELLITE_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
    private readonly SATELLITE_ATTRIBUTION = '© Esri';

    // HERE Traffic API v7
    private readonly HERE_API_KEY = environment.heremapsTourPlanningCredentials.apiKey;
    private readonly TRAFFIC_FLOW_API_URL = 'https://data.traffic.hereapi.com/v7/flow';
    private readonly TRAFFIC_INCIDENTS_API_URL = 'https://data.traffic.hereapi.com/v7/incidents';

    private generateId(): string {
        return `map-obj-${++this.idCounter}`;
    }

    initialize(element: HTMLElement, options: MapOptions): void {
        if (this.map) {
            this.destroy();
        }

        // Configure Leaflet default icon paths before creating the map
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'assets/leaflet/images/marker-icon-2x.png',
            iconUrl: 'assets/leaflet/images/marker-icon.png',
            shadowUrl: 'assets/leaflet/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
        });

        this.map = L.map(element, {
            center: [options.center.lat, options.center.lng],
            zoom: options.zoom,
            minZoom: options.minZoom ?? 2,
            maxZoom: options.maxZoom ?? 19,
            zoomControl: false, // Disable default zoom control, we'll add it manually
            attributionControl: options.attributionControl ?? true
        });

        // Create base layers
        const osmLayer = L.tileLayer(this.OSM_TILE_URL, {
            attribution: this.OSM_ATTRIBUTION,
            maxZoom: 19
        });

        const cleanOsmLayer = L.tileLayer(this.CLEAN_OSM_TILE_URL, {
            attribution: this.CLEAN_OSM_ATTRIBUTION,
            maxZoom: 19,
            subdomains: 'abcd'
        });

        const satelliteLayer = L.tileLayer(this.SATELLITE_TILE_URL, {
            attribution: this.SATELLITE_ATTRIBUTION,
            maxZoom: 19
        });

        this.baseLayers.set('osm', osmLayer);
        this.baseLayers.set('cleanOsm', cleanOsmLayer);
        this.baseLayers.set('satellite', satelliteLayer);

        // Add default layer (OSM)
        this.currentBaseLayer = osmLayer;
        osmLayer.addTo(this.map);

        // Create traffic feature groups (not added to map by default)
        this.trafficFlowGroup = new L.FeatureGroup();
        this.trafficIncidentsGroup = new L.FeatureGroup();

        // Add custom horizontal control container with all controls
        this.addHorizontalControlContainer(options);

        // Initialize drawn items layer for polygon drawing
        this.drawnItems = new L.FeatureGroup();
        this.map.addLayer(this.drawnItems);

        // Close all popups when clicking on the map (outside of popups)
        this.map.on('click', (e: L.LeafletMouseEvent) => {
            // Only close if clicking directly on the map, not on markers
            if (
                (e.originalEvent.target as HTMLElement).classList.contains('leaflet-container') ||
                (e.originalEvent.target as HTMLElement).closest('.leaflet-tile-pane') ||
                (e.originalEvent.target as HTMLElement).closest('.leaflet-overlay-pane')
            ) {
                this.map?.closePopup();
            }
        });

        // Track popup open/close state (Leaflet doesn't expose isPopupOpen() in all versions)
        this.map.on('popupopen', () => {
            this.popupOpen = true;
        });
        this.map.on('popupclose', () => {
            this.popupOpen = false;
        });
    }

    destroy(): void {
        if (this.map) {
            this.map.off();
            this.map.remove();
            this.map = null;
        }
        this.markers.clear();
        this.polylines.clear();
        this.polygons.clear();
        this.groups.clear();
        this.markerClusterGroup = null;
        this.drawnItems = null;
        this.drawControl = null;
        this.layerControl = null;
        this.scaleControl = null;
        this.baseLayers.clear();
        this.currentBaseLayer = null;
        this.trafficFlowGroup = null;
        this.trafficIncidentsGroup = null;
        this.trafficFlowEnabled = false;
        this.trafficIncidentsEnabled = false;
        if (this.trafficFetchDebounceTimer) {
            clearTimeout(this.trafficFetchDebounceTimer);
            this.trafficFetchDebounceTimer = null;
        }
        this.trafficFlowAbortController?.abort();
        this.trafficFlowAbortController = null;
        this.trafficIncidentsAbortController?.abort();
        this.trafficIncidentsAbortController = null;
        this.eventHandlers.clear();
        this.clusteringType = ClusteringType.COUNT;
        this.valueExtractor = null;
    }

    private addHorizontalControlContainer(options: MapOptions): void {
        if (!this.map) return;

        const HorizontalControls = L.Control.extend({
            options: {
                position: 'topleft'
            },

            onAdd: (map: L.Map) => {
                // Create main container
                const mainContainer = L.DomUtil.create('div', 'leaflet-control-horizontal-container');
                mainContainer.style.display = 'flex';
                mainContainer.style.flexDirection = 'row';
                mainContainer.style.gap = '8px';
                mainContainer.style.alignItems = 'flex-start';

                // Add zoom control if enabled
                if (options.zoomControl !== false) {
                    const zoomContainer = this.createZoomControl(map);
                    mainContainer.appendChild(zoomContainer);
                }

                // Add layer control if enabled
                if (options.layerControl !== false) {
                    const layerContainer = this.createLayerControl(map);
                    mainContainer.appendChild(layerContainer);
                }

                // Add scale control if enabled
                if (options.scaleControl !== false) {
                    const scaleContainer = this.createScaleControl(map);
                    mainContainer.appendChild(scaleContainer);
                }

                // Prevent map interactions on control
                L.DomEvent.disableClickPropagation(mainContainer);
                L.DomEvent.disableScrollPropagation(mainContainer);

                return mainContainer;
            }
        });

        const horizontalControls = new HorizontalControls();
        horizontalControls.addTo(this.map);
    }

    /**
     * Create zoom control element
     */
    private createZoomControl(map: L.Map): HTMLElement {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-zoom');

        const zoomInButton = L.DomUtil.create('a', 'leaflet-control-zoom-in', container);
        zoomInButton.href = '#';
        zoomInButton.title = 'Zoom in';
        zoomInButton.innerHTML = '+';
        zoomInButton.setAttribute('role', 'button');
        zoomInButton.setAttribute('aria-label', 'Zoom in');

        const zoomOutButton = L.DomUtil.create('a', 'leaflet-control-zoom-out', container);
        zoomOutButton.href = '#';
        zoomOutButton.title = 'Zoom out';
        zoomOutButton.innerHTML = '−';
        zoomOutButton.setAttribute('role', 'button');
        zoomOutButton.setAttribute('aria-label', 'Zoom out');

        L.DomEvent.on(zoomInButton, 'click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            map.zoomIn();
        });

        L.DomEvent.on(zoomOutButton, 'click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            map.zoomOut();
        });

        return container;
    }

    /**
     * Create layer switcher control element
     */
    private createLayerControl(map: L.Map): HTMLElement {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom-layers');
        container.style.background = 'white';
        container.style.cursor = 'pointer';
        container.style.padding = '0';
        container.style.borderRadius = '4px';
        container.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        container.style.position = 'relative';
        container.style.zIndex = '1001';

        // Create the button
        const button = L.DomUtil.create('button', 'layer-control-button', container);
        button.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                <polyline points="2 17 12 22 22 17"></polyline>
                <polyline points="2 12 12 17 22 12"></polyline>
            </svg>
        `;
        button.style.border = 'none';
        button.style.background = 'transparent';
        button.style.width = '36px';
        button.style.height = '36px';
        button.style.display = 'flex';
        button.style.alignItems = 'center';
        button.style.justifyContent = 'center';
        button.style.cursor = 'pointer';
        button.style.color = '#333';
        button.title = 'Choose view';

        // Create dropdown menu
        const menu = L.DomUtil.create('div', 'layer-control-menu', container);
        menu.style.display = 'none';
        menu.style.position = 'absolute';
        menu.style.top = '42px';
        menu.style.left = '0';
        menu.style.background = '#2d2d2d';
        menu.style.borderRadius = '8px';
        menu.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
        menu.style.padding = '8px 0';
        menu.style.minWidth = '200px';
        menu.style.zIndex = '1002';

        // Create menu items
        const mapViewOption = this.createLayerOption('Map view', 'osm', true);
        const cleanMapViewOption = this.createLayerOption('Clean map', 'cleanOsm', false);
        const satelliteOption = this.createLayerOption('Satellite', 'satellite', false);

        menu.appendChild(mapViewOption);
        menu.appendChild(cleanMapViewOption);
        menu.appendChild(satelliteOption);

        // Add separator between base layers and traffic overlays
        const separator = document.createElement('div');
        separator.style.height = '1px';
        separator.style.background = 'rgba(255,255,255,0.15)';
        separator.style.margin = '8px 16px';
        menu.appendChild(separator);

        // Add overlay section header
        const overlayHeader = document.createElement('div');
        overlayHeader.style.padding = '4px 16px';
        overlayHeader.style.color = '#888';
        overlayHeader.style.fontSize = '11px';
        overlayHeader.style.textTransform = 'uppercase';
        overlayHeader.style.letterSpacing = '0.5px';
        overlayHeader.textContent = 'Overlays';
        menu.appendChild(overlayHeader);

        // Add traffic flow toggle
        const trafficFlowToggle = this.createOverlayToggle('Traffic conditions', this.trafficFlowEnabled, (enabled: boolean) =>
            this.toggleTrafficFlow(enabled)
        );
        menu.appendChild(trafficFlowToggle);

        // Add traffic incidents toggle
        const trafficIncidentsToggle = this.createOverlayToggle('Traffic incidents', this.trafficIncidentsEnabled, (enabled: boolean) =>
            this.toggleTrafficIncidents(enabled)
        );
        menu.appendChild(trafficIncidentsToggle);

        // Toggle menu on button click
        L.DomEvent.on(button, 'click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            const isVisible = menu.style.display === 'block';
            menu.style.display = isVisible ? 'none' : 'block';
        });

        // Close menu when clicking outside
        L.DomEvent.on(map.getContainer(), 'click', () => {
            menu.style.display = 'none';
        });

        return container;
    }

    /**
     * Create scale control element
     */
    private createScaleControl(map: L.Map): HTMLElement {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom-scale');
        container.style.background = 'rgba(255, 255, 255, 0.5)';
        container.style.padding = '4px 8px';
        container.style.borderRadius = '4px';
        container.style.cursor = 'pointer';
        container.style.fontSize = '11px';
        container.style.fontWeight = '500';
        container.style.boxShadow = '0 1px 5px rgba(0,0,0,0.15)';
        container.style.userSelect = 'none';
        container.style.backdropFilter = 'blur(4px)';

        let isMetric = true;

        const updateScale = () => {
            const zoom = map.getZoom();
            const center = map.getCenter();
            const pointA = map.project(center, zoom);
            const pointB = L.point(pointA.x + 100, pointA.y);
            const latLngA = map.unproject(pointA, zoom);
            const latLngB = map.unproject(pointB, zoom);
            const distance = latLngA.distanceTo(latLngB);

            let displayDistance: number;
            let unit: string;
            let width: number;

            if (isMetric) {
                // Metric (kilometers)
                if (distance > 1000) {
                    displayDistance = Math.round(distance / 1000);
                    unit = 'km';
                } else {
                    displayDistance = Math.round(distance);
                    unit = 'm';
                }
            } else {
                // Imperial (miles)
                const miles = distance * 0.000621371;
                if (miles >= 1) {
                    displayDistance = Math.round(miles);
                    unit = 'mi';
                } else {
                    displayDistance = Math.round(miles * 5280);
                    unit = 'ft';
                }
            }

            // Adjust the scale to nice round numbers
            const scales = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
            let scaleDistance = scales[0];
            for (const scale of scales) {
                if (displayDistance >= scale) {
                    scaleDistance = scale;
                } else {
                    break;
                }
            }

            width = (scaleDistance / displayDistance) * 100;

            container.innerHTML = `
                <div style="display: flex; align-items: center; gap: 8px;">
                    <div style="display: flex; flex-direction: column; align-items: flex-start;">
                        <div style="width: ${width}px; height: 2px; background: #333; margin-bottom: 2px;"></div>
                        <div style="color: #333; font-size: 11px;">${scaleDistance} ${unit}</div>
                    </div>
                </div>
            `;
        };

        // Toggle between km and miles on click
        L.DomEvent.on(container, 'click', (e) => {
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            isMetric = !isMetric;
            updateScale();
        });

        // Update scale on zoom/move
        map.on('zoomend moveend', updateScale);

        // Initial update
        updateScale();

        return container;
    }

    private createLayerOption(label: string, layerKey: string, isActive: boolean): HTMLElement {
        const option = document.createElement('div');
        option.className = 'layer-option' + (isActive ? ' active' : '');
        option.style.padding = '12px 16px';
        option.style.cursor = 'pointer';
        option.style.color = isActive ? '#fff' : '#b0b0b0';
        option.style.fontSize = '14px';
        option.style.transition = 'all 0.2s';
        option.textContent = label;

        option.addEventListener('mouseenter', () => {
            if (!option.classList.contains('active')) {
                option.style.background = 'rgba(255,255,255,0.1)';
            }
        });

        option.addEventListener('mouseleave', () => {
            if (!option.classList.contains('active')) {
                option.style.background = 'transparent';
            }
        });

        option.addEventListener('click', (e) => {
            e.stopPropagation();
            this.switchLayer(layerKey);

            // Update active state
            const parent = option.parentElement;
            if (parent) {
                Array.from(parent.children).forEach((child) => {
                    child.classList.remove('active');
                    (child as HTMLElement).style.color = '#b0b0b0';
                    (child as HTMLElement).style.background = 'transparent';
                });
            }
            option.classList.add('active');
            option.style.color = '#fff';
        });

        return option;
    }

    /**
     * Create an overlay toggle option (checkbox style) for the layer control dropdown
     */
    private createOverlayToggle(label: string, initialState: boolean, onChange: (enabled: boolean) => void): HTMLElement {
        const option = document.createElement('div');
        option.style.padding = '10px 16px';
        option.style.cursor = 'pointer';
        option.style.color = initialState ? '#fff' : '#b0b0b0';
        option.style.fontSize = '14px';
        option.style.transition = 'all 0.2s';
        option.style.display = 'flex';
        option.style.alignItems = 'center';
        option.style.gap = '10px';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.checked = initialState;
        checkbox.style.accentColor = '#4CAF50';
        checkbox.style.width = '16px';
        checkbox.style.height = '16px';
        checkbox.style.cursor = 'pointer';
        checkbox.style.margin = '0';
        checkbox.style.flexShrink = '0';

        const labelSpan = document.createElement('span');
        labelSpan.textContent = label;

        option.appendChild(checkbox);
        option.appendChild(labelSpan);

        const updateVisual = () => {
            option.style.color = checkbox.checked ? '#fff' : '#b0b0b0';
        };

        option.addEventListener('mouseenter', () => {
            option.style.background = 'rgba(255,255,255,0.1)';
        });
        option.addEventListener('mouseleave', () => {
            option.style.background = 'transparent';
        });

        option.addEventListener('click', (e) => {
            e.stopPropagation();
            checkbox.checked = !checkbox.checked;
            updateVisual();
            onChange(checkbox.checked);
        });

        checkbox.addEventListener('click', (e) => {
            e.stopPropagation();
            updateVisual();
            onChange(checkbox.checked);
        });

        return option;
    }

    /**
     * Switch between base layers
     */
    private switchLayer(layerKey: string): void {
        if (!this.map) return;

        const newLayer = this.baseLayers.get(layerKey);
        if (!newLayer || newLayer === this.currentBaseLayer) return;

        // Remove current layer
        if (this.currentBaseLayer) {
            this.map.removeLayer(this.currentBaseLayer);
        }

        // Add new layer
        this.currentBaseLayer = newLayer;
        newLayer.addTo(this.map);
    }

    /**
     * Toggle traffic flow overlay on/off
     */
    private toggleTrafficFlow(enabled: boolean): void {
        if (!this.map || !this.trafficFlowGroup) return;
        this.trafficFlowEnabled = enabled;
        if (enabled) {
            this.trafficFlowGroup.addTo(this.map);
            this.fetchTrafficFlow();
            this.bindTrafficRefresh();
        } else {
            this.map.removeLayer(this.trafficFlowGroup);
            this.trafficFlowAbortController?.abort();
            this.trafficFlowAbortController = null;
            this.unbindTrafficRefreshIfNeeded();
        }
    }

    /**
     * Toggle traffic incidents overlay on/off
     */
    private toggleTrafficIncidents(enabled: boolean): void {
        if (!this.map || !this.trafficIncidentsGroup) return;
        this.trafficIncidentsEnabled = enabled;
        if (enabled) {
            this.trafficIncidentsGroup.addTo(this.map);
            this.fetchTrafficIncidents();
            this.bindTrafficRefresh();
        } else {
            this.map.removeLayer(this.trafficIncidentsGroup);
            this.trafficIncidentsAbortController?.abort();
            this.trafficIncidentsAbortController = null;
            this.unbindTrafficRefreshIfNeeded();
        }
    }

    /**
     * Bind map moveend event to refresh traffic data when viewport changes
     */
    private bindTrafficRefresh(): void {
        if (!this.map) return;
        // Only bind once - check if already bound
        if ((this.map as any)._trafficRefreshBound) return;
        (this.map as any)._trafficRefreshBound = true;
        this.map.on('moveend', this.onTrafficViewportChange, this);
    }

    /**
     * Unbind traffic refresh if neither flow nor incidents are enabled
     */
    private unbindTrafficRefreshIfNeeded(): void {
        if (!this.map) return;
        if (!this.trafficFlowEnabled && !this.trafficIncidentsEnabled) {
            this.map.off('moveend', this.onTrafficViewportChange, this);
            (this.map as any)._trafficRefreshBound = false;
        }
    }

    /**
     * Debounced handler for viewport changes - re-fetches active traffic data
     */
    private onTrafficViewportChange(): void {
        if (this.trafficFetchDebounceTimer) {
            clearTimeout(this.trafficFetchDebounceTimer);
        }
        this.trafficFetchDebounceTimer = setTimeout(() => {
            if (this.trafficFlowEnabled) this.fetchTrafficFlow();
            if (this.trafficIncidentsEnabled) this.fetchTrafficIncidents();
        }, 500);
    }

    /**
     * Split the current viewport into bbox strings of max 0.9° (under HERE's 1° limit).
     * Returns an array of bbox strings in format: west,south,east,north
     */
    private getViewportBboxTiles(): string[] {
        if (!this.map) return [];
        const bounds = this.map.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        const maxDeg = 0.9; // Stay under HERE's 1-degree limit
        const bboxes: string[] = [];

        for (let lat = sw.lat; lat < ne.lat; lat += maxDeg) {
            for (let lng = sw.lng; lng < ne.lng; lng += maxDeg) {
                const south = lat;
                const north = Math.min(lat + maxDeg, ne.lat);
                const west = lng;
                const east = Math.min(lng + maxDeg, ne.lng);
                bboxes.push(`${west},${south},${east},${north}`);
            }
        }

        return bboxes;
    }

    /**
     * Fetch traffic flow data from HERE Traffic API v7 and render as colored polylines.
     * Splits viewport into sub-boxes to respect HERE's 1° bbox limit.
     */
    private fetchTrafficFlow(): void {
        if (!this.map || !this.trafficFlowGroup) return;

        const bboxes = this.getViewportBboxTiles();
        if (!bboxes.length) return;

        // Abort previous requests
        this.trafficFlowAbortController?.abort();
        this.trafficFlowAbortController = new AbortController();
        const signal = this.trafficFlowAbortController.signal;

        this.trafficFlowGroup.clearLayers();

        const fetchPromises = bboxes.map((bbox) => {
            const url = `${this.TRAFFIC_FLOW_API_URL}?locationReferencing=shape&in=bbox:${bbox}&apiKey=${this.HERE_API_KEY}`;
            return fetch(url, {signal})
                .then((response) => response.json())
                .then((data) => {
                    if (!this.trafficFlowGroup || signal.aborted) return;
                    if (data.results) {
                        data.results.forEach((item: any) => {
                            const currentFlow = item.currentFlow;
                            if (item.location?.shape?.links && currentFlow) {
                                // Skip free-flow roads (jamFactor < 0.5) to reduce clutter
                                if (currentFlow.jamFactor < 0.5) return;
                                const {color, weight, opacity} = this.getTrafficFlowStyle(currentFlow.jamFactor);
                                item.location.shape.links.forEach((link: any) => {
                                    if (link.points?.length) {
                                        const points = link.points.map((p: any) => L.latLng(p.lat, p.lng));
                                        const polyline = L.polyline(points, {color, weight, opacity});
                                        this.trafficFlowGroup!.addLayer(polyline);
                                    }
                                });
                            }
                        });
                    }
                });
        });

        Promise.all(fetchPromises).catch((err) => {
            if (err.name !== 'AbortError') {
                console.error('Failed to fetch traffic flow:', err);
            }
        });
    }

    /**
     * Fetch traffic incidents from HERE Traffic API v7 and render as markers.
     * Splits viewport into sub-boxes to respect HERE's 1° bbox limit.
     */
    private fetchTrafficIncidents(): void {
        if (!this.map || !this.trafficIncidentsGroup) return;

        const bboxes = this.getViewportBboxTiles();
        if (!bboxes.length) return;

        // Abort previous requests
        this.trafficIncidentsAbortController?.abort();
        this.trafficIncidentsAbortController = new AbortController();
        const signal = this.trafficIncidentsAbortController.signal;

        this.trafficIncidentsGroup.clearLayers();

        const fetchPromises = bboxes.map((bbox) => {
            const url = `${this.TRAFFIC_INCIDENTS_API_URL}?locationReferencing=shape&in=bbox:${bbox}&apiKey=${this.HERE_API_KEY}`;
            return fetch(url, {signal})
                .then((response) => response.json())
                .then((data) => {
                    if (!this.trafficIncidentsGroup || signal.aborted) return;
                    if (data.results) {
                        data.results.forEach((item: any) => {
                            const incidentDetails = item.incidentDetails;
                            const location = item.location;
                            if (!location?.shape?.links?.length) return;

                            // Collect all points for this incident to place one marker
                            let allPoints: L.LatLng[] = [];

                            location.shape.links.forEach((link: any) => {
                                if (link.points?.length) {
                                    const points = link.points.map((p: any) => L.latLng(p.lat, p.lng));
                                    allPoints = allPoints.concat(points);
                                }
                            });

                            if (!allPoints.length) return;

                            // Place a single small icon at the midpoint of the incident
                            const midIdx = Math.floor(allPoints.length / 2);
                            const midPoint = allPoints[midIdx];
                            const incidentIcon = this.createIncidentIcon(incidentDetails?.type);
                            const marker = L.marker(midPoint, {icon: incidentIcon});
                            marker.bindPopup(this.buildIncidentPopup(incidentDetails), {
                                className: 'traffic-incident-popup',
                                maxWidth: 280,
                                minWidth: 220
                            });
                            this.trafficIncidentsGroup!.addLayer(marker);
                        });
                    }
                });
        });

        Promise.all(fetchPromises).catch((err) => {
            if (err.name !== 'AbortError') {
                console.error('Failed to fetch traffic incidents:', err);
            }
        });
    }

    /**
     * Get traffic flow line style based on jamFactor.
     * Lower jamFactor = thinner, more transparent (subtle).
     * Higher jamFactor = thicker, more opaque (attention-grabbing).
     */
    private getTrafficFlowStyle(jamFactor: number): {color: string; weight: number; opacity: number} {
        if (jamFactor < 2) return {color: '#4caf50', weight: 3, opacity: 0.6}; // Green - light flow
        if (jamFactor < 4) return {color: '#8bc34a', weight: 3.5, opacity: 0.65}; // Yellow-green
        if (jamFactor < 6) return {color: '#ff9800', weight: 4, opacity: 0.7}; // Orange - moderate
        if (jamFactor < 8) return {color: '#f44336', weight: 4.5, opacity: 0.75}; // Red - heavy
        return {color: '#b71c1c', weight: 5, opacity: 0.85}; // Dark red - severe
    }

    /**
     * Create a subtle SVG-based icon for traffic incidents
     */
    private createIncidentIcon(type?: string): L.DivIcon {
        const svgIcon = this.getIncidentSvg(type);
        return L.divIcon({
            className: 'traffic-incident-icon',
            html: `<div style="
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
            ">${svgIcon}</div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -14]
        });
    }

    /**
     * Get SVG icon markup for incident type (matching HERE Maps style)
     */
    private getIncidentSvg(type?: string): string {
        switch (type) {
            case 'CONSTRUCTION':
                return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 22h20L12 2z" fill="#f59e0b" stroke="#d97706" stroke-width="1"/>
                    <path d="M12 9v5M12 16v1" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                </svg>`;
            case 'ACCIDENT':
                return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#ef4444" stroke="#dc2626" stroke-width="1"/>
                    <path d="M12 7v5M12 14.5v1" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                </svg>`;
            case 'ROAD_CLOSURE':
                return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#dc2626" stroke="#b91c1c" stroke-width="1"/>
                    <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                </svg>`;
            case 'LANE_RESTRICTION':
                return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="2" width="20" height="20" rx="3" fill="#f59e0b" stroke="#d97706" stroke-width="1"/>
                    <path d="M12 7v5M12 14.5v1" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                </svg>`;
            default:
                return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 22h20L12 2z" fill="#6b7280" stroke="#4b5563" stroke-width="1"/>
                    <path d="M12 9v5M12 16v1" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                </svg>`;
        }
    }

    /**
     * Build a styled popup HTML for traffic incidents (matching HERE Maps style)
     */
    private buildIncidentPopup(details: any): string {
        const type = details?.type || 'INCIDENT';
        const typeLabel = this.formatIncidentType(type);
        const description = details?.description?.value || 'Traffic incident';
        const startTime = details?.startTime ? this.formatDateTime(details.startTime) : null;
        const endTime = details?.endTime ? this.formatDateTime(details.endTime) : null;
        const headerColor = this.getIncidentHeaderColor(type);

        let html = `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: -10px -16px; line-height: 1.4;">
                <div style="background: ${headerColor}; color: white; padding: 8px 14px; font-weight: 600; font-size: 13px; border-radius: 8px 8px 0 0;">
                    ${typeLabel}
                </div>
                <div style="padding: 10px 14px; font-size: 12px; color: #333;">
                    <div style="margin-bottom: 8px;">${description}</div>`;

        if (startTime || endTime) {
            html += `<table style="width: 100%; font-size: 11px; color: #555; border-collapse: collapse;">`;
            if (startTime) {
                html += `<tr>
                    <td style="padding: 2px 0; white-space: nowrap; color: #888;">Start time:</td>
                    <td style="padding: 2px 0 2px 12px; text-align: right;">${startTime}</td>
                </tr>`;
            }
            if (endTime) {
                html += `<tr>
                    <td style="padding: 2px 0; white-space: nowrap; color: #888;">Estimated end time:</td>
                    <td style="padding: 2px 0 2px 12px; text-align: right;">${endTime}</td>
                </tr>`;
            }
            html += `</table>`;
        }

        html += `</div></div>`;
        return html;
    }

    /**
     * Format incident type enum to display label
     */
    private formatIncidentType(type: string): string {
        switch (type) {
            case 'CONSTRUCTION':
                return 'Construction';
            case 'ACCIDENT':
                return 'Accident';
            case 'ROAD_CLOSURE':
                return 'Road Closure';
            case 'LANE_RESTRICTION':
                return 'Lane Restriction';
            case 'ROAD_HAZARD':
                return 'Road Hazard';
            case 'WEATHER':
                return 'Weather';
            case 'PLANNED_EVENT':
                return 'Planned Event';
            case 'CONGESTION':
                return 'Congestion';
            default:
                return type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, ' ');
        }
    }

    /**
     * Get header background color for incident popup
     */
    private getIncidentHeaderColor(type: string): string {
        switch (type) {
            case 'CONSTRUCTION':
                return '#78716c';
            case 'ACCIDENT':
                return '#78716c';
            case 'ROAD_CLOSURE':
                return '#78716c';
            case 'LANE_RESTRICTION':
                return '#78716c';
            default:
                return '#78716c';
        }
    }

    /**
     * Format ISO datetime to readable string
     */
    private formatDateTime(isoString: string): string {
        try {
            const date = new Date(isoString);
            return (
                date.toLocaleDateString('en-GB', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                }) +
                ' ' +
                date
                    .toLocaleTimeString('en-GB', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    })
                    .toUpperCase()
            );
        } catch {
            return isoString;
        }
    }

    setTrafficFlowVisible(visible: boolean): void {
        this.toggleTrafficFlow(visible);
    }

    setTrafficIncidentsVisible(visible: boolean): void {
        this.toggleTrafficIncidents(visible);
    }

    getMap(): L.Map | null {
        return this.map;
    }

    setCenter(lat: number, lng: number): void {
        this.map?.setView([lat, lng]);
    }

    setZoom(zoom: number): void {
        this.map?.setZoom(zoom);
    }

    getCenter(): LatLng | null {
        if (!this.map) return null;
        const center = this.map.getCenter();
        return {lat: center.lat, lng: center.lng};
    }

    getZoom(): number | null {
        return this.map?.getZoom() ?? null;
    }

    setView(lat: number, lng: number, zoom: number): void {
        this.map?.setView([lat, lng], zoom);
    }

    fitBounds(bounds: MapBounds, padding?: number): void {
        if (!this.map) return;
        const leafletBounds = L.latLngBounds([
            [bounds.south, bounds.west],
            [bounds.north, bounds.east]
        ]);
        this.map.fitBounds(leafletBounds, {padding: [padding ?? 50, padding ?? 50]});
    }

    fitBoundsToMarkers(padding?: number): void {
        if (!this.map || this.markers.size === 0) return;

        const group = L.featureGroup(Array.from(this.markers.values()));
        this.map.fitBounds(group.getBounds(), {padding: [padding ?? 50, padding ?? 50]});
    }

    fitBoundsToGroup(groupId: string, padding?: number): void {
        if (!this.map) return;
        const group = this.groups.get(groupId);
        if (group && group.getLayers().length > 0) {
            this.map.fitBounds(group.getBounds(), {padding: [padding ?? 50, padding ?? 50]});
        }
    }

    addMarker(options: MarkerOptions): string {
        if (!this.map) return '';

        const id = this.generateId();
        let icon: L.Icon | L.DivIcon;

        // Support shorthand iconHtml property
        const iconHtml = options.iconHtml || options.icon?.html;
        const iconAnchor = options.iconAnchor || options.icon?.iconAnchor;

        if (iconHtml) {
            // Custom HTML marker (equivalent to HERE Maps DomIcon)
            // Use iconSize: null to let CSS control the size (otherwise Leaflet defaults to 12x12)
            icon = L.divIcon({
                html: iconHtml,
                className: options.icon?.className || 'custom-div-marker',
                iconSize: options.icon?.iconSize ? L.point(options.icon.iconSize[0], options.icon.iconSize[1]) : (null as any),
                iconAnchor: iconAnchor ? L.point(iconAnchor[0], iconAnchor[1]) : (null as any),
                popupAnchor: options.icon?.popupAnchor ? L.point(options.icon.popupAnchor[0], options.icon.popupAnchor[1]) : [0, -10]
            });
        } else if (options.icon?.iconUrl) {
            icon = L.icon({
                iconUrl: options.icon.iconUrl,
                iconSize: options.icon.iconSize,
                iconAnchor: options.icon.iconAnchor,
                popupAnchor: options.icon.popupAnchor
            });
        } else {
            // Default Leaflet marker
            icon = new L.Icon.Default();
        }

        const marker = L.marker([options.lat, options.lng], {
            icon,
            draggable: options.draggable ?? false,
            zIndexOffset: options.zIndex ?? 0
        });

        if (options.draggable) {
            marker.on('drag', () => {
                const latlng = marker.getLatLng();
                this.emitEvent({
                    type: 'markerdrag',
                    latlng: {lat: latlng.lat, lng: latlng.lng}
                });
            });
        }

        // Store custom data and popup generator
        if (options.data) {
            (marker as any).data = options.data;
        }
        if (options.popupGenerator) {
            (marker as any).popupGenerator = options.popupGenerator;
        }

        // For markers with popup generator, create popup on-demand in click handler
        // For static popups, bind normally
        if (options.popup && !options.popupGenerator) {
            marker.bindPopup(options.popup, {
                autoClose: true,
                closeOnClick: false,
                maxWidth: 700,
                className: 'custom-leaflet-popup',
                ...this.popupPanPadding
            });
        } else if (options.popupGenerator) {
            // Don't bind popup initially - open on mouseover, close only on click or when another opens
        }

        // Add to cluster group if clustering is enabled, otherwise to map/group
        if (this.markerClusterGroup) {
            this.markerClusterGroup.addLayer(marker);
        } else if (options.groupId && this.groups.has(options.groupId)) {
            this.groups.get(options.groupId)!.addLayer(marker);
        } else {
            marker.addTo(this.map);
        }

        const generator = (marker as any).popupGenerator;
        if (generator) {
            // Uniform with cluster: open on mouseover, close only on click or when another opens
            const openPopupForMarker = () => {
                const data = (marker as any).data;
                if (!data) return;
                this.map?.closePopup();
                const freshContent = generator(data);
                if (marker.getPopup()) {
                    marker.unbindPopup();
                }
                marker.bindPopup(freshContent, {
                    autoClose: true,
                    closeOnClick: false,
                    maxWidth: 700,
                    className: 'custom-leaflet-popup',
                    ...this.popupPanPadding
                });
                marker.openPopup();
            };
            marker.on('mouseover', openPopupForMarker);
            marker.on('click', (e: L.LeafletMouseEvent) => {
                L.DomEvent.stopPropagation(e.originalEvent);
                if (this.isTouchOnly) {
                    openPopupForMarker();
                } else {
                    marker.closePopup();
                }
                this.emitEvent({
                    type: 'markerclick',
                    latlng: {lat: e.latlng.lat, lng: e.latlng.lng},
                    target: marker,
                    data: (marker as any).data
                });
            });
        } else {
            marker.on('click', (e) => {
                this.emitEvent({
                    type: 'markerclick',
                    latlng: {lat: e.latlng.lat, lng: e.latlng.lng},
                    target: marker,
                    data: (marker as any).data
                });
            });
        }

        this.markers.set(id, marker);
        return id;
    }

    removeMarker(id: string): void {
        const marker = this.markers.get(id);
        if (marker) {
            if (this.markerClusterGroup) {
                this.markerClusterGroup.removeLayer(marker);
            } else {
                marker.remove();
            }
            this.markers.delete(id);
        }
    }

    updateMarkerPosition(id: string, lat: number, lng: number): void {
        const marker = this.markers.get(id);
        if (marker) {
            marker.setLatLng([lat, lng]);
        }
    }

    getMarkerPosition(id: string): LatLng {
        const marker = this.markers.get(id);
        if (!marker) return null;
        const latlng = marker.getLatLng();
        return {lat: latlng.lat, lng: latlng.lng};
    }

    updateMarkerIcon(id: string, iconHtml: string): void {
        const marker = this.markers.get(id);
        if (marker && iconHtml) {
            const currentIcon = marker.getIcon();
            const className = currentIcon instanceof L.DivIcon ? currentIcon.options.className : 'custom-div-marker';
            const iconAnchor = currentIcon instanceof L.DivIcon ? currentIcon.options.iconAnchor : null;
            const popupAnchor = currentIcon instanceof L.DivIcon ? currentIcon.options.popupAnchor : [0, -10];

            const newIcon = L.divIcon({
                html: iconHtml,
                className: className,
                iconSize: null as any,
                iconAnchor: iconAnchor as any,
                popupAnchor: popupAnchor as any
            });

            // Store popup state before updating
            const wasPopupOpen = marker.isPopupOpen();
            const popup = marker.getPopup();
            let popupContent: string | null = null;
            if (popup) {
                const content = popup.getContent();
                if (typeof content === 'string') {
                    popupContent = content;
                } else if (content && typeof content === 'object' && 'outerHTML' in content) {
                    popupContent = (content as HTMLElement).outerHTML;
                }
                // For function-based content, we can't easily extract it, so skip restoring
            }
            const popupOptions = popup ? popup.options : null;

            marker.setIcon(newIcon);

            // If marker is in a cluster, refresh the cluster icon by removing and re-adding
            // This triggers the iconCreateFunction to recalculate cluster appearance
            if (this.markerClusterGroup) {
                this.markerClusterGroup.removeLayer(marker);
                this.markerClusterGroup.addLayer(marker);
            }

            // Restore popup binding if it existed (but not if using popupGenerator)
            const popupGenerator = (marker as any).popupGenerator;
            if (popupContent && !popupGenerator) {
                marker.bindPopup(
                    popupContent,
                    popupOptions || {
                        autoClose: true,
                        closeOnClick: false,
                        maxWidth: 700,
                        className: 'custom-leaflet-popup',
                        ...this.popupPanPadding
                    }
                );
            }

            // Re-attach uniform handlers (mouseover open, click close for popupGenerator)
            marker.off('click');
            marker.off('mouseover');
            if (popupGenerator) {
                const openPopupForMarker = () => {
                    const data = (marker as any).data;
                    if (!data) return;
                    this.map?.closePopup();
                    const freshContent = popupGenerator(data);
                    if (marker.getPopup()) {
                        marker.unbindPopup();
                    }
                    marker.bindPopup(freshContent, {
                        autoClose: true,
                        closeOnClick: false,
                        maxWidth: 700,
                        className: 'custom-leaflet-popup',
                        ...this.popupPanPadding
                    });
                    marker.openPopup();
                };
                marker.on('mouseover', openPopupForMarker);
                marker.on('click', (e: L.LeafletMouseEvent) => {
                    L.DomEvent.stopPropagation(e.originalEvent);
                    if (this.isTouchOnly) {
                        openPopupForMarker();
                    } else {
                        marker.closePopup();
                    }
                    this.emitEvent({
                        type: 'markerclick',
                        latlng: {lat: e.latlng.lat, lng: e.latlng.lng},
                        target: marker,
                        data: (marker as any).data
                    });
                });
            } else {
                marker.on('click', (e) => {
                    this.emitEvent({
                        type: 'markerclick',
                        latlng: {lat: e.latlng.lat, lng: e.latlng.lng},
                        target: marker,
                        data: (marker as any).data
                    });
                });
            }

            // Reopen popup if it was open before (uniform: hover opened it)
            if (wasPopupOpen) {
                setTimeout(() => {
                    if (popupGenerator) {
                        marker.fire('mouseover');
                    } else {
                        marker.openPopup();
                    }
                }, 0);
            }
        }
    }

    updateMarkerPopup(id: string, popupContent: string): void {
        const marker = this.markers.get(id);
        if (marker && popupContent) {
            const wasPopupOpen = marker.isPopupOpen();
            marker.bindPopup(popupContent, {
                autoClose: true,
                closeOnClick: false,
                maxWidth: 700,
                className: 'custom-leaflet-popup',
                ...this.popupPanPadding
            });
            // If popup was open, reopen it with new content
            if (wasPopupOpen) {
                marker.openPopup();
            }
        }
    }

    clearMarkers(): void {
        if (this.markerClusterGroup) {
            this.markerClusterGroup.clearLayers();
        }
        this.markers.forEach((marker) => marker.remove());
        this.markers.clear();
    }

    getMarkerData(id: string): any {
        const marker = this.markers.get(id);
        return marker ? (marker as any).data : null;
    }

    openMarkerPopup(id: string): void {
        const marker = this.markers.get(id);
        if (marker) {
            // If marker has a popup generator, regenerate content with current data
            const popupGenerator = (marker as any).popupGenerator;
            if (popupGenerator) {
                const data = (marker as any).data;
                const freshContent = popupGenerator(data);
                marker.bindPopup(freshContent, {
                    autoClose: true,
                    closeOnClick: false,
                    maxWidth: 700,
                    className: 'custom-leaflet-popup',
                    ...this.popupPanPadding
                });
            } else {
                // Ensure popup content is refreshed before opening
                // This is important for dynamic content that may have changed
                const currentPopup = marker.getPopup();
                if (currentPopup) {
                    // Rebind popup to ensure fresh content
                    const content = currentPopup.getContent();
                    marker.unbindPopup();
                    marker.bindPopup(content, {
                        autoClose: true,
                        closeOnClick: false,
                        maxWidth: 700,
                        className: 'custom-leaflet-popup',
                        ...this.popupPanPadding
                    });
                }
            }
            marker.openPopup();
        }
    }

    closeMarkerPopup(id: string): void {
        const marker = this.markers.get(id);
        if (marker) {
            marker.closePopup();
        }
    }

    enableClustering(options?: ClusterOptions): void {
        if (!this.map || this.markerClusterGroup) return;

        // Store clustering options for use in icon creation
        this.clusteringType = options?.clusteringType ?? ClusteringType.COUNT;
        this.valueExtractor = options?.valueExtractor ?? null;

        // Determine the icon creation function based on clustering type
        const iconCreateFunction = options?.iconCreateFunction ?? this.createDefaultClusterIcon.bind(this);

        this.markerClusterGroup = L.markerClusterGroup({
            chunkedLoading: true,
            maxClusterRadius: options?.maxRadius ?? 80,
            spiderfyOnMaxZoom: options?.spiderfyOnMaxZoom ?? true,
            showCoverageOnHover: options?.showCoverageOnHover ?? false,
            zoomToBoundsOnClick: options?.zoomToBoundsOnClick ?? true,
            iconCreateFunction
        });

        // Add cluster event listeners if callbacks are provided
        if (options?.onClusterClick) {
            this.markerClusterGroup.on('clusterclick', (e: any) => {
                options.onClusterClick!(e.layer);
            });
        }

        if (options?.onClusterMouseOver) {
            this.markerClusterGroup.on('clustermouseover', (e: any) => {
                const latLng = e.layer.getLatLng();
                options.onClusterMouseOver!(e.layer, {lat: latLng.lat, lng: latLng.lng});
            });
        }

        if (options?.onClusterMouseOut) {
            this.markerClusterGroup.on('clustermouseout', () => {
                options.onClusterMouseOut!();
            });
        }

        this.map.addLayer(this.markerClusterGroup);

        // Move existing markers to cluster group
        this.markers.forEach((marker) => {
            this.map!.removeLayer(marker);
            this.markerClusterGroup!.addLayer(marker);
        });

        // Handle click events for individual markers within clusters
        // MarkerCluster fires 'click' on child markers when they're spiderfied
        this.markerClusterGroup.on('click', (e: any) => {
            const marker = e.layer;

            const generator = (marker as any).popupGenerator;

            if (generator) {
                // Generate fresh content for clustered marker
                const data = (marker as any).data;

                if (data) {
                    const freshContent = generator(data);

                    // Unbind old popup if exists and bind fresh one
                    if (marker.getPopup()) {
                        marker.unbindPopup();
                    }

                    marker.bindPopup(freshContent, {
                        autoClose: true,
                        closeOnClick: false,
                        maxWidth: 700,
                        className: 'custom-leaflet-popup',
                        ...this.popupPanPadding
                    });

                    marker.openPopup();
                }
            }
        });
    }

    /**
     * Creates the default cluster icon based on the clustering type
     * - COUNT: Shows number of markers in cluster
     * - SUM: Sums values from marker data using valueExtractor
     */
    private createDefaultClusterIcon(cluster: any): L.DivIcon {
        let displayValue: number;

        if (this.clusteringType === ClusteringType.SUM && this.valueExtractor) {
            // SUM clustering: sum the values from all child markers
            displayValue = 0;
            const markers = cluster.getAllChildMarkers();
            markers.forEach((marker: any) => {
                // Data is stored at marker.data in Leaflet (set in addMarker)
                const data = (marker as any).data ?? marker.options?.data;
                if (data && this.valueExtractor) {
                    displayValue += this.valueExtractor(data);
                }
            });
        } else {
            // COUNT clustering: just count the markers
            displayValue = cluster.getChildCount();
        }

        // Determine size based on display value
        let size = 'small';
        let dimension = 30;
        if (displayValue > 100) {
            size = 'large';
            dimension = 50;
        } else if (displayValue > 10) {
            size = 'medium';
            dimension = 40;
        }

        return L.divIcon({
            html: `<div class="marker-cluster-${size}"><span>${displayValue}</span></div>`,
            className: 'marker-cluster',
            iconSize: L.point(dimension, dimension)
        });
    }

    /**
     * Get the current clustering type
     */
    getClusteringType(): ClusteringType {
        return this.clusteringType;
    }

    /**
     * Get the value extractor function for SUM clustering
     */
    getValueExtractor(): ((markerData: any) => number) | null {
        return this.valueExtractor;
    }

    disableClustering(): void {
        if (!this.map || !this.markerClusterGroup) return;

        // Move markers back to map
        this.markerClusterGroup.eachLayer((layer) => {
            if (layer instanceof L.Marker) {
                layer.addTo(this.map!);
            }
        });

        this.map.removeLayer(this.markerClusterGroup);
        this.markerClusterGroup = null;

        // Reset clustering options
        this.clusteringType = ClusteringType.COUNT;
        this.valueExtractor = null;
    }

    /**
     * Rebuild cluster layer - no-op for Leaflet as it handles dynamic clustering automatically
     */
    rebuildClusterLayer(): void {
        // Leaflet's markerClusterGroup handles dynamic clustering automatically
        // No rebuild needed
    }

    addPolyline(options: PolylineOptions): string {
        if (!this.map) return '';

        const id = this.generateId();
        const latLngs = options.coordinates.map((c) => [c.lat, c.lng] as L.LatLngTuple);

        const polyline = L.polyline(latLngs, {
            color: options.color ?? '#0080ff',
            weight: options.weight ?? 8,
            opacity: options.opacity ?? 0.9,
            dashArray: options.dashArray
        }).addTo(this.map);

        this.polylines.set(id, polyline);
        return id;
    }

    /**
     * Add polyline from encoded string (GraphHopper format)
     */
    addPolylineFromEncoded(encoded: string, options?: PolylineStyleOptions): string {
        const coordinates = decodePolyline(encoded);
        return this.addPolyline({
            coordinates,
            color: options?.color ?? '#0080ff',
            weight: options?.weight ?? 6,
            opacity: options?.opacity ?? 0.8,
            dashArray: options?.dashArray
        });
    }

    /**
     * Add multiple polyline segments from comma-separated encoded strings
     */
    addMultiPolylineFromEncoded(encodedSegments: string, options?: PolylineStyleOptions): string {
        if (!this.map) return '';

        const id = this.generateId();
        const segments = decodeMultiplePolylines(encodedSegments);
        const polylines: L.Polyline[] = [];

        segments.forEach((coords) => {
            const latLngs = toLeafletCoordinates(coords);
            const polyline = L.polyline(latLngs, {
                color: options?.color ?? '#0080ff',
                weight: options?.weight ?? 5,
                opacity: options?.opacity ?? 1,
                dashArray: options?.dashArray
            }).addTo(this.map!);
            polylines.push(polyline);
        });

        this.polylines.set(id, polylines);
        return id;
    }

    removePolyline(id: string): void {
        const polyline = this.polylines.get(id);
        if (polyline) {
            if (Array.isArray(polyline)) {
                polyline.forEach((p) => p.remove());
            } else {
                polyline.remove();
            }
            this.polylines.delete(id);
        }
    }

    clearPolylines(): void {
        this.polylines.forEach((polyline) => {
            if (Array.isArray(polyline)) {
                polyline.forEach((p) => p.remove());
            } else {
                polyline.remove();
            }
        });
        this.polylines.clear();
    }

    fitBoundsToPolyline(id: string, padding?: number): void {
        if (!this.map) return;
        const polyline = this.polylines.get(id);
        if (polyline) {
            if (Array.isArray(polyline)) {
                const group = L.featureGroup(polyline);
                this.map.fitBounds(group.getBounds(), {padding: [padding ?? 50, padding ?? 50]});
            } else {
                this.map.fitBounds(polyline.getBounds(), {padding: [padding ?? 50, padding ?? 50]});
            }
        }
    }

    addPolygon(coordinates: LatLng[], options?: PolygonOptions): string {
        if (!this.map) return '';

        const id = this.generateId();
        const latLngs = coordinates.map((c) => [c.lat, c.lng] as L.LatLngTuple);

        const polygon = L.polygon(latLngs, {
            color: options?.strokeColor ?? '#2cabbb',
            weight: options?.strokeWeight ?? 2,
            fillColor: options?.fillColor ?? 'rgba(44, 171, 184, 0.8)',
            fillOpacity: options?.fillOpacity ?? 0.8
        });

        if (this.drawnItems) {
            this.drawnItems.addLayer(polygon);
        } else {
            polygon.addTo(this.map);
        }

        this.polygons.set(id, polygon);
        return id;
    }

    removePolygon(id: string): void {
        const polygon = this.polygons.get(id);
        if (polygon) {
            if (this.drawnItems) {
                this.drawnItems.removeLayer(polygon);
            } else {
                polygon.remove();
            }
            this.polygons.delete(id);
        }
    }

    clearPolygons(): void {
        this.polygons.forEach((polygon) => {
            if (this.drawnItems) {
                this.drawnItems.removeLayer(polygon);
            } else {
                polygon.remove();
            }
        });
        this.polygons.clear();
    }

    getPolygonCoordinates(id: string): LatLng[] | null {
        const polygon = this.polygons.get(id);
        if (!polygon) return null;

        const latLngs = polygon.getLatLngs()[0] as L.LatLng[];
        return latLngs.map((ll) => ({lat: ll.lat, lng: ll.lng}));
    }

    fitBoundsToPolygon(id: string, padding?: number): void {
        if (!this.map) return;
        const polygon = this.polygons.get(id);
        if (polygon) {
            this.map.fitBounds(polygon.getBounds(), {padding: [padding ?? 50, padding ?? 50]});
        }
    }

    enablePointMovement(pointId: string) {}

    enablePolygonDrawing(
        polygonId: string,
        onComplete: (coordinates: LatLng[], polygonId?: string) => void,
        onEdit?: (coordinates: LatLng[]) => void
    ): void {
        if (!this.map || !this.drawnItems) return;
        if (this.drawControl) {
            this.map.removeControl(this.drawControl);
            this.drawControl = null;
            this.map.off(L.Draw.Event.CREATED);
            this.map.off(L.Draw.Event.EDITED);
            this.map.off(L.Draw.Event.DELETED);
        }

        this.drawControl = new L.Control.Draw({
            position: 'topleft',
            draw: {
                polygon: {
                    allowIntersection: true,
                    showArea: true,
                    shapeOptions: {
                        color: '#2cabbb',
                        fillColor: 'rgba(44, 171, 184, 0.8)',
                        fillOpacity: 0.8
                    }
                },
                polyline: false,
                circle: false,
                rectangle: false,
                marker: false,
                circlemarker: false
            },
            edit: {
                featureGroup: this.drawnItems,
                remove: true,
                edit: {}
            }
        });

        this.map.addControl(this.drawControl);

        // Handle new polygon creation
        this.map.on(L.Draw.Event.CREATED, (e: any) => {
            const layer = e.layer;
            this.drawnItems!.addLayer(layer);

            if (layer instanceof L.Polygon) {
                const latLngs = layer.getLatLngs()[0] as L.LatLng[];
                const coordinates = latLngs.map((ll) => ({lat: ll.lat, lng: ll.lng}));

                // Store the polygon
                const id = this.generateId();
                this.polygons.set(id, layer);

                onComplete(coordinates, id);

                this.emitEvent({
                    type: 'polygoncreated',
                    data: {id, coordinates}
                });
            }
        });

        // Handle polygon edit
        this.map.on(L.Draw.Event.EDITED, (e: any) => {
            const layers = e.layers;
            layers.eachLayer((layer: L.Polygon) => {
                const latLngs = layer.getLatLngs()[0] as L.LatLng[];
                const coordinates = latLngs.map((ll) => ({lat: ll.lat, lng: ll.lng}));

                if (onEdit) {
                    onEdit(coordinates);
                }

                this.emitEvent({
                    type: 'polygonedited',
                    data: {coordinates}
                });
            });
        });

        // Handle polygon delete
        this.map.on(L.Draw.Event.DELETED, (e: any) => {
            const layers = e.layers;
            layers.eachLayer((layer: L.Polygon) => {
                // Find and remove from our map
                this.polygons.forEach((polygon, id) => {
                    if (polygon === layer) {
                        this.polygons.delete(id);
                    }
                });
            });
        });
    }

    completePolygonDrawing(): void {
        const controlAny = this.drawControl as any;
        try {
            if (controlAny._toolbars?.edit) {
                const editToolbar = controlAny._toolbars.edit;
                if (editToolbar._activeMode?.handler && editToolbar._activeMode.handler.enabled()) {
                    editToolbar._activeMode.handler.save();
                }
            }
        } catch (e) {
            return;
        }
    }

    disablePolygonDrawing(): void {
        if (!this.map || !this.drawControl) return;

        this.map.removeControl(this.drawControl);
        this.drawControl = null;

        // Remove draw event listeners
        this.map.off(L.Draw.Event.CREATED);
        this.map.off(L.Draw.Event.EDITED);
        this.map.off(L.Draw.Event.DELETED);
    }

    showPopup(lat: number, lng: number, content: string | HTMLElement): void {
        if (!this.map) return;

        L.popup({
            autoClose: true,
            closeOnClick: false,
            maxWidth: 850,
            minWidth: 750,
            className: 'custom-leaflet-popup',
            ...this.popupPanPadding
        })
            .setLatLng([lat, lng])
            .setContent(content)
            .openOn(this.map);
    }

    closeAllPopups(): void {
        this.map?.closePopup();
    }

    isAnyPopupOpen(): boolean {
        return this.popupOpen;
    }

    createGroup(id: string): void {
        if (!this.map || this.groups.has(id)) return;

        const group = L.featureGroup().addTo(this.map);
        this.groups.set(id, group);
    }

    addToGroup(groupId: string, objectId: string): void {
        const group = this.groups.get(groupId);
        if (!group) return;

        const marker = this.markers.get(objectId);
        if (marker) {
            group.addLayer(marker);
        }
    }

    clearGroup(groupId: string): void {
        const group = this.groups.get(groupId);
        if (group) {
            group.clearLayers();
        }
    }

    removeGroup(groupId: string): void {
        const group = this.groups.get(groupId);
        if (group) {
            group.remove();
            this.groups.delete(groupId);
        }
    }

    getGroupBounds(groupId: string): MapBounds | null {
        const group = this.groups.get(groupId);
        if (!group || group.getLayers().length === 0) return null;

        const bounds = group.getBounds();
        return {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
        };
    }

    on(event: MapEventType, handler: (e: MapEvent) => void): void {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event)!.push(handler);

        // Also bind to Leaflet map events
        if (this.map) {
            const leafletEvent = this.mapEventToLeaflet(event);
            if (leafletEvent) {
                this.map.on(leafletEvent, (e: L.LeafletEvent) => {
                    this.emitEvent({
                        type: event,
                        latlng: (e as any).latlng ? {lat: (e as any).latlng.lat, lng: (e as any).latlng.lng} : undefined,
                        originalEvent: (e as any).originalEvent
                    });
                });
            }
        }
    }

    off(event: MapEventType, handler?: (e: MapEvent) => void): void {
        if (!handler) {
            this.eventHandlers.delete(event);
            const leafletEvent = this.mapEventToLeaflet(event);
            if (leafletEvent && this.map) {
                this.map.off(leafletEvent);
            }
        } else {
            const handlers = this.eventHandlers.get(event);
            if (handlers) {
                const index = handlers.indexOf(handler);
                if (index > -1) {
                    handlers.splice(index, 1);
                }
            }
        }
    }

    private emitEvent(event: MapEvent): void {
        const handlers = this.eventHandlers.get(event.type);
        if (handlers) {
            handlers.forEach((handler) => handler(event));
        }
    }

    private mapEventToLeaflet(event: MapEventType): string | null {
        const mapping: Record<string, string> = {
            click: 'click',
            dblclick: 'dblclick',
            drag: 'drag',
            dragstart: 'dragstart',
            dragend: 'dragend',
            moveend: 'moveend',
            zoomend: 'zoomend'
        };
        return mapping[event] ?? null;
    }

    invalidateSize(): void {
        this.map?.invalidateSize();
    }

    panTo(lat: number, lng: number): void {
        this.map?.panTo([lat, lng]);
    }

    /**
     * Remove all objects from the map
     */
    clearAll(): void {
        this.clearMarkers();
        this.clearPolylines();
        this.clearPolygons();
        this.groups.forEach((group) => group.clearLayers());
    }
}
